import { IEmployeeViewModel } from 'Interfaces/IEmployeeConfig';
import {
  EnumBillingType,
  EnumBoard,
  EnumJobType,
  EnumPriority,
  EnumSupportBilling,
  EnumTicketStatus,
  IMobileAnswersViewModel,
} from 'Interfaces/IGlobalTypeConfig';
import {
  ITicketBillingViewModel,
  ITicketCompanyViewModel,
  ITicketPostViewModel,
} from 'Interfaces/ITicketConfig';

export interface IBillingState {
  editedTicket: ITicketBillingViewModel;
  totalBillingValue: number;
}

export const selectedBillingPostInitialState: ITicketBillingViewModel = {
  id: '',
  ticketNo: '',
  board: {} as EnumBoard,
  priority: {} as EnumPriority,
  status: {} as EnumTicketStatus,
  company: {} as ITicketCompanyViewModel,
  creator: {} as IEmployeeViewModel,
  contact: {} as IEmployeeViewModel,
  responsible: {} as IEmployeeViewModel,
  shippingCost: 0,
  deliveryStreet: '',
  deliveryPostalCode: '',
  deliveryArea: '',
  deliveryCity: '',
  deliveryCountry: '',
  deliveryPostbox: '',
  attention: '',
  scheduled: null as any, // eller dayjs() hvis du vil ha default nåtid
  deliveryDate: null as any,
  ETCHours: 0,
  ETCMinutes: 0,
  jobType: {} as EnumJobType,
  supportType: {} as IMobileAnswersViewModel,
  errorDescription: null,
  companyProductWithError: null,
  technicians: [],
  comment: '',
  fixedHourlyRate: 0,
  fixedTime: 0,
  fixedPrice: 0,
  maxTime: 0,
  maxPrice: 0,
  productBilling: false,
  updated: null as any,
  created: null as any,
  billing: {} as EnumBillingType,
  title: '',
  ticketProducts: [],
  supportBilling: {} as EnumSupportBilling,
  ticketPosts: [],
  consumableProducts: [],
};

export const EditBillingState: IBillingState = {
  editedTicket: selectedBillingPostInitialState,
  totalBillingValue: 0,
};

export type EditBillingAction =
  | {
      type: 'UPDATE_TICKET_POST';
      payload: { index: number; field: keyof ITicketPostViewModel; value: any };
    }
  | { type: 'REMOVE_CONSUMABLE_PRODUCT'; payload: { index: number } }
  | {
      type: 'UPDATE_TICKET_FIELD';
      payload: { field: keyof ITicketBillingViewModel; value: any };
    }
  | { type: 'SET_TOTAL_BILLING_VALUE'; payload: { totalBillingValue: number } };

export const EditBillingReducer = (
  state: typeof EditBillingState,
  action: EditBillingAction
): typeof EditBillingState => {
  switch (action.type) {
    case 'SET_TOTAL_BILLING_VALUE':
      return {
        ...state,
        totalBillingValue: action.payload.totalBillingValue,
      };

    case 'UPDATE_TICKET_POST': {
      const { index, field, value } = action.payload;

      // Add validation to prevent crashes
      if (
        !state.editedTicket.ticketPosts ||
        index < 0 ||
        index >= state.editedTicket.ticketPosts.length
      ) {
        console.error(
          'Invalid ticket post index:',
          index,
          'Available posts:',
          state.editedTicket.ticketPosts?.length
        );
        return state; // Return current state instead of crashing
      }

      const updatedPosts = [...state.editedTicket.ticketPosts];
      const targetPost = updatedPosts[index];

      // Ensure the post exists
      if (!targetPost) {
        console.error('Ticket post not found at index:', index);
        return state;
      }

      updatedPosts[index] = {
        ...targetPost,
        [field]: value,
        isChanged: true,
      };

      return {
        ...state,
        editedTicket: {
          ...state.editedTicket,
          ticketPosts: updatedPosts,
        },
      };
    }

    case 'REMOVE_CONSUMABLE_PRODUCT':
      const updatedProducts = state.editedTicket.consumableProducts.map(
        (p, i) =>
          i === action.payload.index ? { ...p, isActive: !p.isActive } : p
      );

      return {
        ...state,
        editedTicket: {
          ...state.editedTicket,
          consumableProducts: updatedProducts,
        },
      };

    case 'UPDATE_TICKET_FIELD':
      return {
        ...state,
        editedTicket: {
          ...state.editedTicket,
          [action.payload.field]: action.payload.value,
        },
      };

    default:
      return state;
  }
};
